import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Equal, Sparkles } from 'lucide-react'
import { useAudio } from '../../hooks/useAudio'
import { useSparkle } from '../../hooks/useSparkle'

interface SimpleAdditionProps {
  num1: number
  num2: number
  onComplete: (correct: boolean) => void
}

export default function SimpleAddition({ num1, num2, onComplete }: SimpleAdditionProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [countdown, setCountdown] = useState(0)
  
  const { playCorrect, playWrong } = useAudio()
  const { miniBurst } = useSparkle()

  const correctAnswer = num1 + num2
  
  // Generate wrong answers (±1, ±2 from correct)
  const wrongAnswers = [
    Math.max(1, correctAnswer - 2),
    Math.max(1, correctAnswer - 1),
    correctAnswer + 1,
    correctAnswer + 2,
  ].filter(n => n !== correctAnswer && n > 0)
  
  // Select 3 unique wrong answers
  const uniqueWrongs = wrongAnswers.slice(0, 3)
  const options = [...uniqueWrongs, correctAnswer].sort(() => Math.random() - 0.5)

  const handleSelect = useCallback((answer: number) => {
    if (showFeedback) return

    const correct = answer === correctAnswer
    setSelectedAnswer(answer)
    setIsCorrect(correct)
    setShowFeedback(true)
    
    // Play sound and effects
    if (correct) {
      playCorrect()
      // Sparkle effect at center of screen
      miniBurst(0.5, 0.5)
    } else {
      playWrong()
    }
    
    // Differentiated timing: wrong answers get more time
    const feedbackDuration = correct ? 1200 : 2000
    setCountdown(Math.ceil(feedbackDuration / 1000))
    
    setTimeout(() => {
      onComplete(correct)
    }, feedbackDuration)
  }, [onComplete, showFeedback, correctAnswer, playCorrect, playWrong, miniBurst])
  
  // Countdown timer for visual feedback
  useEffect(() => {
    if (!showFeedback || countdown <= 0) return
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(timer)
  }, [showFeedback, countdown])

  return (
    <div className="w-full max-w-sm mx-auto px-3">
      {/* Problem Display - Compact for mobile */}
      <motion.div 
        className="flex items-center justify-center gap-2 mb-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {/* First Number */}
        <motion.div 
          className="w-14 h-14 sm:w-16 sm:h-16 bg-[var(--math-surface-elevated)] rounded-xl flex items-center justify-center shadow-inner"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.05, duration: 0.2 }}
        >
          <span className="text-2xl sm:text-3xl font-bold text-gray-800">{num1}</span>
        </motion.div>

        {/* Plus Sign */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
        >
          <Plus className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--math-primary)]" strokeWidth={3} />
        </motion.div>

        {/* Second Number */}
        <motion.div 
          className="w-14 h-14 sm:w-16 sm:h-16 bg-[var(--math-surface-elevated)] rounded-xl flex items-center justify-center shadow-inner"
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.05, duration: 0.2 }}
        >
          <span className="text-2xl sm:text-3xl font-bold text-gray-800">{num2}</span>
        </motion.div>

        {/* Equal Sign */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.2 }}
        >
          <Equal className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--math-text-muted)]" strokeWidth={3} />
        </motion.div>

        {/* Question Mark */}
        <motion.div 
          className="w-14 h-14 sm:w-16 sm:h-16 bg-[var(--math-accent)]/20 rounded-xl flex items-center justify-center border-2 border-dashed border-[var(--math-accent)]"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.2 }}
        >
          <span className="text-2xl sm:text-3xl font-bold text-[var(--math-accent)]">?</span>
        </motion.div>
      </motion.div>

      {/* Answer Options - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {options.map((answer, index) => {
          const isSelected = selectedAnswer === answer
          const showAsCorrect = showFeedback && answer === correctAnswer
          const showAsWrong = showFeedback && isSelected && answer !== correctAnswer

          return (
            <motion.button
              key={answer}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
              whileHover={!showFeedback ? { scale: 1.03 } : {}}
              whileTap={!showFeedback ? { scale: 0.97 } : {}}
              onClick={() => handleSelect(answer)}
              disabled={showFeedback}
              className={`
                h-16 sm:h-18 rounded-2xl font-bold text-2xl sm:text-3xl
                flex items-center justify-center
                transition-all duration-150
                ${showAsCorrect 
                  ? 'bg-[var(--math-success)] text-white shadow-lg' 
                  : showAsWrong 
                    ? 'bg-[var(--math-error)] text-white'
                    : isSelected 
                      ? 'bg-[var(--math-primary)] text-white shadow-md'
                      : 'bg-white shadow-sm hover:shadow-md border-2 border-gray-100'
                }
              `}
            >
              {answer}
            </motion.button>
          )
        })}
      </div>

      {/* Enhanced Feedback with Countdown */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center mt-3"
          >
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 bg-[var(--math-success)]/10 px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-[var(--math-success)]" />
                <span className="font-bold text-[var(--math-success)]">{num1} + {num2} = {correctAnswer}!</span>
                <span className="text-xl">🌟</span>
                {/* Small countdown for correct answers */}
                {countdown > 0 && (
                  <span className="text-xs text-[var(--math-success)]/60 ml-1">{countdown}</span>
                )}
              </div>
            ) : (
              <div className="bg-[var(--math-error)]/10 px-3 py-2 rounded-xl">
                {/* Show what they picked vs correct answer */}
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-500">You picked:</span>
                  <span className="font-bold text-[var(--math-error)]">{selectedAnswer}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-500">Correct answer:</span>
                  <span className="font-bold text-[var(--math-success)]">{correctAnswer}</span>
                </div>
                {/* Visual countdown bar for wrong answers */}
                <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[120px] mx-auto">
                  <motion.div
                    className="h-full bg-[var(--math-error)]"
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: 2, ease: 'linear' }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
